import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  TrendingDown as DeficitIcon,
  Inventory2 as InventoryIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useHomeWidgetCompras } from "../../hooks/useDistributionCoverage";

export interface WidgetHomeComprasProps {
  onProductClick?: (productId: string) => void;
}

/**
 * Widget para el home de compras: cobertura agregada del sistema, top
 * tiendas con déficit, top productos atascados, contadores de denials,
 * diffs pendientes y alertas de subcompra.
 *
 * Refresca automáticamente cada 2 minutos (configurado en el hook).
 */
export function WidgetHomeCompras({
  onProductClick,
}: WidgetHomeComprasProps): React.ReactElement {
  const { data, isLoading, isError, refetch } = useHomeWidgetCompras(true);

  return (
    <Card variant="outlined" sx={{ minHeight: 280 }}>
      <CardHeader
        title="Cobertura del sistema"
        subheader="Distribución a tiendas"
        action={
          <Tooltip title="Refrescar">
            <IconButton onClick={refetch} size="small" disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error">
            No se pudo cargar el widget. Reintenta o revisa el endpoint
            /api/compras/home-widget.
          </Alert>
        )}

        {data && !isLoading && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                icon={<DeficitIcon />}
                label={`${data.total_pending_denials} denegaciones`}
                color={data.total_pending_denials > 50 ? "error" : "default"}
              />
              <Chip
                icon={<InventoryIcon />}
                label={`${data.total_qty_denied.toFixed(0)} u sin cubrir`}
                variant="outlined"
              />
              {data.diffs_pending_capture > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${data.diffs_pending_capture} diffs por capturar`}
                  color="warning"
                />
              )}
              {data.subcompra_alerts > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${data.subcompra_alerts} alertas subcompra`}
                  color="error"
                />
              )}
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tiendas con más déficit
              </Typography>
              {data.top_5_stores_with_deficits.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin tiendas con déficit detectado.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {data.top_5_stores_with_deficits.map((s) => (
                    <Chip
                      key={s.store}
                      label={`${s.store}: ${s.total_qty_denied.toFixed(0)} u`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Productos atascados (stock central, sin distribuir)
              </Typography>
              {data.top_10_atascados.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay productos con stock atascado.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {data.top_10_atascados.map((p) => (
                    <Box
                      key={p.unified_product_id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: onProductClick ? "pointer" : "default",
                        py: 0.25,
                        "&:hover": onProductClick
                          ? { backgroundColor: "action.hover" }
                          : {},
                      }}
                      onClick={() =>
                        onProductClick?.(p.unified_product_id)
                      }
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={p.unified_product_id}
                      >
                        {p.unified_product_id}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={`${p.pending_qty.toFixed(0)} u`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${p.stores} tiendas`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${p.oldest_days}d`}
                          size="small"
                          color={p.oldest_days > 30 ? "error" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
